import { Input } from '@/common/components/Input.tsx'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { SubmitHandler, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from 'react-router-dom';
import { AppRoutePaths } from '@/routing/route_paths'
import { Label } from '@/shad/ui/label'
import { useSelector, useDispatch } from "react-redux"
import { RootState } from '@/storage/store'
import { setReimbursementAccountRoutingNumber } from '@/storage/applications/reimubrusementAccountSlice'
import FamilyWizardLayout from '@/app/pages/family_application/FamilyWizardLayout'
import { SubmitButton } from '@/app/pages/family_application/student_details/shared/submit-button'
import RoutingNumberHelpIMG from '@assets/routing_number.png'
import { Button } from '@/app/common/components/Button'

const detailSchema = z
  .object({
    routing_number: z.string().min(1, 'routing_number_is_required').regex(/^\d{1,9}$/, 'routing_number_must_be_up_to_9_digits'),
  })

type FormValues = z.infer<typeof detailSchema>

export const ReimbursementRoutingNumber = () => {
  const { t: translate } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { currentAccount } = useSelector((state: RootState) => state.ReimbursementAccount)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(detailSchema),
    defaultValues: {
      routing_number: currentAccount?.routingNumber ?? "",
    }
  })

  const onSubmit: SubmitHandler<FormValues> = async (data: FormValues) => {
    dispatch(setReimbursementAccountRoutingNumber(data.routing_number))
    navigate(AppRoutePaths.reimbursements.accounts.link.bank)
  }

  const onBack = () => {
    navigate(-1)
  }

  return (
    <FamilyWizardLayout>
      <FamilyWizardLayout.Header>
        <h1>{translate('whats_you_bank_routing_number')}</h1>
      </FamilyWizardLayout.Header>
      <FamilyWizardLayout.Content className="w-100">
        <form
          id="form"
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-3"
        >
          <div className="my-10 sm:px-20">
            <section
              className="flex flex-col mt-6 space-y-2"
              aria-labelledby="routing_number_label"
            >
              <Label
                id="routing_number_label"
                htmlFor="routing_number"
              >
                {translate('routing_number')}{' '}
                <span className="text-danger ml-1">*</span>
              </Label>
              <div className="relative mt-2 rounded-md shadow-sm">
                <Input
                  id="routing_number"
                  {...register('routing_number')}
                />
              </div>
              {errors.routing_number && (
                <p className="mt-1 text-sm text-red-600">
                  {translate(errors.routing_number.message ?? '')}
                </p>
              )}
            </section>

          </div>
          <div>
            <img
              alt="Account number"
              width="100%"
              src={RoutingNumberHelpIMG}
              className="mx-auto h-auto mt-5"
            />
            <div className="font-medium text-gray-900 mt-2 text-left text-xs sm:text-base sm:ml-5">
              {translate('routing_number')}
            </div>
          </div>
        </form>
      </FamilyWizardLayout.Content>
      <FamilyWizardLayout.ActionBar className="justify-center">
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={onBack}
            className="px-10"
          >
            {translate('back')}
          </Button>
          {' '}          <SubmitButton
            form="form"
            status={'idle'}
            buttonTxt={translate('next')}
            className="px-10"
          />
        </div>
      </FamilyWizardLayout.ActionBar>
    </FamilyWizardLayout>
  )
}
